import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Bot, Send, Coins, Plus } from "lucide-react";

export default function DakotaChat() {
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  const loadStatus = () => api.get("/dakota/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    loadStatus();
    api.get("/dakota/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openSession = async (sid) => {
    setSessionId(sid);
    const r = await api.get(`/dakota/sessions/${sid}/messages`);
    setMessages(r.data);
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/dakota/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("natur_token")}` },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error de Dakota");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          let event = "message", data = "";
          for (const line of part.split("\n")) {
            if (line.startsWith("event: ")) event = line.slice(7);
            else if (line.startsWith("data: ")) data = line.slice(6);
          }
          if (event === "session") {
            const sid = JSON.parse(data);
            if (!sessionId) { setSessionId(sid); api.get("/dakota/sessions").then((r) => setSessions(r.data)); }
          } else if (event === "message" && data) {
            const chunk = JSON.parse(data);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      toast.error(e.message);
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
      loadStatus();
    }
  };

  return (
    <div className="fade-up" data-testid="dakota-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-orange-600 text-white flex items-center justify-center"><Bot size={22} /></div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Dakota IA</h1>
            <p className="text-sm text-neutral-500">Super Profesor experto en normativa DGT y seguridad vial</p>
          </div>
        </div>
        {status && (
          <div className="flex items-center gap-2 text-sm font-semibold bg-amber-100 text-amber-800 rounded-full px-4 py-2" data-testid="dakota-credits">
            <Coins size={15} /> {status.credits} créditos
          </div>
        )}
      </div>

      {status && !status.available && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm" data-testid="dakota-disabled-banner">{status.reason}</div>
      )}

      <div className="grid lg:grid-cols-[220px_1fr] gap-4">
        <div className="hidden lg:block space-y-1">
          <button data-testid="dakota-new-chat" onClick={() => { setSessionId(null); setMessages([]); }}
                  className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors">
            <Plus size={15} /> Nueva conversación
          </button>
          {sessions.map((s) => (
            <button key={s.id} onClick={() => openSession(s.id)}
                    className={`w-full text-left text-xs px-3 py-2 rounded-lg truncate transition-colors ${sessionId === s.id ? "bg-orange-100 text-orange-800" : "hover:bg-neutral-100 text-neutral-600"}`}>
              {s.title}
            </button>
          ))}
        </div>

        <div className="bg-white border border-neutral-200 rounded-2xl dakota-glow flex flex-col h-[65vh]">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="text-center text-neutral-400 mt-16">
                <Bot size={40} className="mx-auto mb-3 text-orange-300" />
                <p className="text-sm">Pregúntame sobre señales, normativa, prioridades, maniobras…<br />Las preguntas frecuentes ya respondidas no consumen créditos.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-neutral-900 text-white" : "bg-orange-50 border border-orange-100 text-neutral-800"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? "Dakota está escribiendo…" : "")}
                  {m.cached && <span className="block mt-1 text-[10px] text-orange-500">Respuesta reutilizada · 0 créditos</span>}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-neutral-200 p-4 flex gap-2">
            <input data-testid="dakota-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Escribe tu pregunta a Dakota…"
                   disabled={streaming || (status && !status.available)}
                   className="flex-1 h-11 rounded-full border border-neutral-300 px-5 text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
            <Button data-testid="dakota-send-btn" onClick={send} disabled={streaming || (status && !status.available)}
                    className="rounded-full bg-orange-600 hover:bg-orange-700 text-white h-11 w-11 p-0">
              <Send size={17} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
